import { Link } from 'react-router-dom'

import type { UserDrawerTab } from './use-activity-params'

import { Segmented } from '@/components/activity/activity-panel'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet'
import { UserActivityPane } from '@/components/user-activity/user-activity-pane'
import { UserAuditFeed } from '@/components/user-activity/user-audit-feed'
import { RoutePaths } from '@/routes/routes'
import { useQueryGetUserById } from '@/services'
import { getInitials } from '@/utils/activity'

export type DrawerUser = {
  user_id: string
  name?: string | null
  email?: string | null
}

type UserActivityDrawerProps = {
  orgId: string
  /** identity from the activity lists, if the user is part of them */
  user: DrawerUser | null
  tab: UserDrawerTab
  onTabChange: (tab: UserDrawerTab) => void
  onClose: () => void
}

export const UserActivityDrawer = ({
  orgId,
  user,
  tab,
  onTabChange,
  onClose
}: UserActivityDrawerProps) => {
  const userId = user?.user_id
  // identity fallback when the user is not part of the loaded lists (deep link)
  const { data: fetchedUser } = useQueryGetUserById(userId ?? '', orgId, {
    enabled: !!userId && !user?.name && !user?.email
  })

  const name = user?.name || fetchedUser?.display_name
  const email = user?.email || fetchedUser?.email

  return (
    <Sheet onOpenChange={(open) => !open && onClose()} open={!!userId}>
      <SheetContent
        className="w-full overflow-y-auto bg-background p-0 text-foreground sm:max-w-2xl"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        {userId && (
          <>
            <SheetHeader className="space-y-4 border-b border-border/40 p-6 text-left">
              <div className="flex items-center gap-4 pr-8">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-secondary/20 text-base font-semibold text-secondary">
                  {getInitials(name, email)}
                </div>
                <div className="min-w-0 space-y-0.5">
                  <SheetTitle className="truncate">
                    <Link
                      className="underline-offset-4 hover:underline"
                      to={`${RoutePaths.USERS}/${userId}?orgId=${orgId}`}
                    >
                      {name || email || 'Unknown user'}
                    </Link>
                  </SheetTitle>
                  <SheetDescription className="truncate text-muted-foreground/80">
                    {email || '—'}
                  </SheetDescription>
                  <p className="font-mono text-xs text-muted-foreground/70">
                    User ID {userId} · Org {orgId}
                  </p>
                </div>
              </div>
              <div className="self-start">
                <Segmented
                  ariaLabel="User details"
                  onChange={onTabChange}
                  options={[
                    { value: 'activity', label: 'Activity' },
                    { value: 'audit', label: 'Audit log' }
                  ]}
                  value={tab}
                />
              </div>
            </SheetHeader>
            <div className="p-6">
              {tab === 'audit' ? (
                <UserAuditFeed orgId={orgId} userId={userId} />
              ) : (
                <UserActivityPane orgId={orgId} userId={userId} />
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
